import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/pt-br';

export type AppLocale = 'pt-br' | 'es';

export const DEFAULT_LOCALE: AppLocale = 'pt-br';

// Ordem = ordem no seletor de idioma (LocalesMenuButton da AppBar e do login).
export const LOCALES: { locale: AppLocale; name: string }[] = [
    { locale: 'pt-br', name: 'Português' },
    { locale: 'es', name: 'Español' },
];

// Guarda só escolha explícita (seletor ou ?lang=). O Store do <Admin> é
// memoryStore(), que perde o idioma no reload; o logout não apaga esta chave.
export const LOCALE_KEY = 'px-locale';

const HTML_LANG: Record<AppLocale, string> = { 'pt-br': 'pt-BR', es: 'es' };

// 'es', 'es-AR', 'es_419' → 'es'; 'pt', 'pt-BR', 'pt-PT' → 'pt-br'; resto → null.
export const normalizeLocale = (value?: string | null): AppLocale | null => {
    const base = (value || '').trim().toLowerCase().split(/[-_]/)[0];
    if (base === 'es') return 'es';
    if (base === 'pt') return 'pt-br';
    return null;
};

// Aceita ?lang=es antes do hash (/?lang=es#/login) ou dentro dele (#/login?lang=es).
const readLangParam = (): string | null => {
    const { search, hash } = window.location;
    const fromSearch = new URLSearchParams(search).get('lang');
    if (fromSearch) return fromSearch;
    const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
    return new URLSearchParams(hashQuery).get('lang');
};

const readStoredLocale = (): string | null => {
    try {
        return localStorage.getItem(LOCALE_KEY);
    } catch {
        return null;
    }
};

export const persistLocale = (locale: AppLocale) => {
    try {
        localStorage.setItem(LOCALE_KEY, locale);
    } catch {
        /* storage bloqueado: segue só em memória */
    }
};

// Idioma do navegador, não IP: navigator.languages já reflete a preferência do
// usuário e não depende de serviço externo de geolocalização.
const readBrowserLocale = (): AppLocale | null => {
    const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const language of languages) {
        const locale = normalizeLocale(language);
        if (locale) return locale;
    }
    return null;
};

// Prioridade: ?lang= (e grava) → escolha salva → navegador → pt-br.
export const resolveInitialLocale = (): AppLocale => {
    const fromUrl = normalizeLocale(readLangParam());
    if (fromUrl) {
        persistLocale(fromUrl);
        return fromUrl;
    }
    return normalizeLocale(readStoredLocale()) ?? readBrowserLocale() ?? DEFAULT_LOCALE;
};

// Idioma corrente fora do contexto do react-admin: o LocalizationProvider do
// App.tsx fica acima do <Admin> e assina via useSyncExternalStore.
let currentLocale: AppLocale = DEFAULT_LOCALE;
const listeners = new Set<() => void>();

export const getAppLocale = () => currentLocale;

export const subscribeAppLocale = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

// Único lugar que troca o locale global do dayjs e do moment. Não chame
// dayjs.locale()/moment.locale() em componente: sobrescreve a escolha do usuário.
export const applyAppLocale = (locale: AppLocale) => {
    currentLocale = locale;
    dayjs.locale(locale);
    moment.locale(locale);
    document.documentElement.lang = HTML_LANG[locale];
    listeners.forEach(listener => listener());
};
